import type { Environment as DeploymentEnvironment } from '@odys/instance-config';

export type { DeploymentEnvironment };

/**
 * Write key du projet Amplitude « Catalogue Production - Odys » (id 100051964,
 * zone EU). Le catalogue a sa propre taxonomie et n'envoie plus rien dans les
 * projets « Odys - Production » (100021092) / « Odys - Staging » (100021505),
 * qui restent ceux du back-office Odys et d'onboarding.
 *
 * La clé est publique par construction — elle part dans le bundle navigateur.
 * Seule la Secret Key du projet est un secret, et elle n'a rien à faire ici.
 *
 * Elle est passée explicitement à `ampli.load({ client: { apiKey } })` plutôt
 * que via `environment` : le type `Environment` généré par Ampli ne connaît
 * que `catalogueproductionodys`. Le projet de staging n'est pas un
 * environnement de la Source catalogue, sa clé ne peut donc passer que par
 * `apiKey`, et la production suit le même chemin.
 */
const CATALOG_PRODUCTION_API_KEY = '1e75f13ca37004ae5409d3adeb0eda9e';

/**
 * Write key du projet Amplitude « Catalogue Staging - Odys » (id 100060134,
 * zone EU). Reçoit la recette et les previews de PR (`*.odys-stag.ing`, que
 * `getEnvironmentFromHostname` résout en `staging`) : une instrumentation se
 * vérifie avant d'atteindre la production, sans en fausser les métriques.
 */
const CATALOG_STAGING_API_KEY = '3b2474092d5928d7c86afa27a4a57bc4';

/**
 * `null` = l'appelant n'initialise pas le SDK du tout : aucun appel réseau,
 * aucun cookie ni localStorage Amplitude en développement ni en test.
 */
const AMPLITUDE_API_KEYS = {
    production: CATALOG_PRODUCTION_API_KEY,
    staging: CATALOG_STAGING_API_KEY,
    local: null,
} satisfies Record<DeploymentEnvironment, string | null>;

export function resolveAmplitudeApiKey(
    env: DeploymentEnvironment,
    isDevelopmentBuild: boolean = process.env.NODE_ENV === 'development',
): string | null {
    // `getEnvironmentFromHostname` déduit l'environnement du header `Host` et
    // retombe sur `production` pour tout hôte inconnu — donc `127.0.0.1`, une IP
    // LAN ou un `Host` absent enverraient des events avec la clé de production.
    // `NODE_ENV` est inliné au build et insensible à l'hôte : c'est le seul
    // verrou fiable pour `next dev`.
    if (isDevelopmentBuild) return null;

    return AMPLITUDE_API_KEYS[env] ?? null;
}
